import { useMemo } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Mail, MessageSquare, Bot, Power, PowerOff } from "lucide-react";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useBranding } from "@/hooks/useBranding";
import { useSetting } from "@/hooks/useAppSettings";
import {
  SEQUENCES,
  MENU_SLUG_BY_EVENT_TYPE,
  NEW_LEAD_SEQUENCE_KEY,
  PARTIAL_LEAD_SEQUENCE_KEY,
  QUIET_HOURS,
  menuUrlForSlug,
  renderStep,
  type SequenceStep,
  type TemplateVars,
} from "../../supabase/functions/_shared/automations/sequences.ts";

// Event types as a lead arrives, in the order they appear on the /plan wizard.
// corporate + drop_off fall through to the default copy variant.
// Wizard leads get the menu link for their service (#1221).
const EVENT_TYPES: { key: string; label: string }[] = [
  { key: "wedding", label: "Wedding" },
  { key: "live_service", label: "Live Service" },
  { key: "venue", label: "Event Space" },
  { key: "corporate", label: "Corporate" },
  { key: "drop_off", label: "Drop-Off" },
];

// #1221: plan-wizard leads get their service's menu link. The real title comes
// from published_menus at send time; the preview uses the card label.
function previewMenuVars(key: string, label: string): Pick<TemplateVars, "menuName" | "menuUrl"> {
  const slug = MENU_SLUG_BY_EVENT_TYPE[key];
  return slug ? { menuName: label, menuUrl: menuUrlForSlug(slug) } : {};
}

type Notification = { key: string; title: string; when: string; live?: boolean };

// Customer quote notifications from epic #985 (#986-#989). They are sent by
// their own triggers, not the sequence engine, so they're listed here by hand.
// None is live yet; set `live` on an entry when it ships.
const CUSTOMER_NOTIFICATIONS: Notification[] = [
  {
    key: "payment_received",
    title: "Payment received",
    when: "When a deposit or balance payment is recorded, whether it comes through Square or is entered by hand. Says how much came in and what's still owed.",
  },
  {
    key: "quote_sent",
    title: "Quote sent",
    when: "When a quote is marked sent. Includes a link to view and accept it online.",
  },
  {
    key: "quote_accepted",
    title: "Quote accepted",
    when: "Right after they accept a quote. Confirms what they approved and what happens next.",
  },
  {
    key: "paid_in_full",
    title: "Paid in full",
    when: "When a payment clears the full balance. Confirms the booking is all set.",
  },
];

// Alerts to the team notifications inbox. The live ones are the `kind`s that
// supabase/functions/team-notify sends (a unit test keeps the two in step).
const TEAM_ALERTS: Notification[] = [
  {
    key: "lead_created",
    title: "New lead",
    when: "When a new request comes in from the website contact form, the event planner or a menu inquiry, including one they'd started earlier and came back to finish. Links to the pipeline.",
    live: true,
  },
  {
    key: "order_placed",
    title: "Online order placed",
    when: "When an online order comes in. Says the order total. It still needs review before payment is taken.",
    live: true,
  },
  {
    key: "quote_accepted",
    title: "Quote accepted",
    when: "When a quote is marked accepted, by the customer or by us.",
    live: true,
  },
  {
    key: "deposit_collected",
    title: "Deposit collected",
    when: "When the first payment is recorded on a quote, even if it covers the whole amount.",
    live: true,
  },
  {
    key: "payment_received",
    title: "Payment received",
    when: "When any later payment is recorded. Says how much has been paid so far.",
    live: true,
  },
  {
    key: "edge_function_auth_error",
    title: "Service access error",
    when: "When one of our back-end services hits a sign-in or permission error, usually an expired or revoked key. One alert per run of errors: repeats from the same service within an hour of the last one are skipped.",
    live: true,
  },
  {
    key: "quote_declined",
    title: "Quote declined",
    when: "When a quote is marked declined, by the customer, by us, or by a Square invoice that was cancelled or refunded. Links straight to it.",
    live: true,
  },
  {
    key: "paid_in_full",
    title: "Paid in full",
    when: "When a payment clears the full balance. Sent along with the deposit or payment alert.",
    live: true,
  },
  {
    key: "lead_flow_alert",
    title: "Ad leads stopped",
    when: "When our Meta ads spent money across the whole check window (48 hours by default) and no paid lead arrived on our site or in Meta, or the monitor couldn't read Meta or our database. One alert per incident.",
    live: true,
  },
  {
    key: "lead_flow_recovered",
    title: "Ad leads back",
    when: "When paid leads show up again, or the monitor can read Meta and our database again, after an ad leads alert.",
    live: true,
  },
  {
    key: "creative_test_digest",
    title: "Creative test digest",
    when: "Once a week while the creative-test agent is on. Lists the posts in test slots, what it would crown or cut and why, and ad spend against the caps.",
    live: true,
  },
  {
    key: "review_recovery",
    title: "Unhappy customer",
    when: "When a customer answers the after-event check saying they weren't happy. Names the event and how to reach them, so someone can follow up. The check itself isn't built yet, so this can't fire until it is.",
    live: true,
  },
  {
    key: "quote_customer_notify_failed",
    title: "Quote email didn't go out",
    when: "When the automatic email with a customer's quote link failed, or nothing was recorded a few minutes after the quote was marked sent. Links to the quote. Sent again every 30 minutes, up to 6 times, until the email to us goes through.",
    live: true,
  },
];

const NEW_LEAD_STEPS = SEQUENCES[NEW_LEAD_SEQUENCE_KEY].steps;

/** "9am" / "8pm" from the engine's quiet-hours config. */
function hourLabel(h: number): string {
  const suffix = h >= 12 ? "pm" : "am";
  return `${h % 12 === 0 ? 12 : h % 12}${suffix}`;
}
const SMS_WINDOW = `${hourLabel(QUIET_HOURS.startHour)} to ${hourLabel(QUIET_HOURS.endHourExclusive)}`;

/** "Day 0", "Day 1"… from a step's offset. */
function dayLabel(step: SequenceStep): string {
  return `Day ${Math.floor(step.offsetHours / 24)}`;
}

/** When the step goes out, in plain words. */
function timingLabel(step: SequenceStep): string {
  if (step.offsetHours === 0) {
    return step.channel === "sms"
      ? `Right away, if they gave a phone number. Texts only go out ${SMS_WINDOW}; overnight leads get it at ${hourLabel(QUIET_HOURS.startHour)}.`
      : "Right away.";
  }
  return `${step.offsetHours} hours after they reach out.`;
}

/** The cadence summary for the card header, e.g. "Day 0: email + text". */
function cadenceSummary(steps: SequenceStep[]): string {
  const byDay = new Map<string, string[]>();
  for (const st of steps) {
    const d = dayLabel(st);
    byDay.set(d, [...(byDay.get(d) ?? []), st.channel === "sms" ? "text" : "email"]);
  }
  return [...byDay.entries()].map(([d, chans]) => `${d}: ${chans.join(" + ")}`).join(" · ");
}

/** A text message shown as a phone bubble. */
function SmsPreview({ message }: { message: string }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="max-w-sm rounded-2xl rounded-bl-sm bg-primary/10 px-4 py-2 text-sm whitespace-pre-line">{message}</div>
      <p className="mt-2 text-xs text-muted-foreground">Sent from Tristano's Main line. Replies land in GHL.</p>
    </div>
  );
}

/** A read-only frame that shows the email exactly as it sends (our own
 * controlled HTML — renderStep escapes all lead-supplied values). */
function EmailPreview({ subject, html }: { subject: string; html: string }) {
  return (
    <div className="rounded-lg border bg-muted/30">
      <div className="border-b px-4 py-2 text-sm">
        <span className="text-muted-foreground">Subject: </span>
        <span className="font-medium">{subject}</span>
      </div>
      <div
        className="px-4 py-3 text-sm [&_a]:text-primary [&_a]:underline [&_p]:mb-2"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

/** A titled card of notifications; unbuilt ones carry a "Coming soon" badge. */
function NotificationCard({ title, recipient, items }: { title: string; recipient: string; items: Notification[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span>{title}</span>
          <Badge variant="secondary" className="font-normal">
            <Mail className="mr-1 h-3 w-3" />
            {recipient}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {items.map((n) => (
            <li key={n.key} data-kind={n.key} className="text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{n.title}</span>
                {!n.live && <Badge variant="outline" className="font-normal">Coming soon</Badge>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{n.when}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** The customer quote-lifecycle notifications. */
function QuoteNotificationsSection() {
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold tracking-tight">When a quote moves forward</h2>
      <NotificationCard title="To the customer" recipient="Email to the address on the quote" items={CUSTOMER_NOTIFICATIONS} />
    </div>
  );
}

/** Alerts to our own inbox. These don't depend on the lead-automation switch. */
function TeamAlertsSection() {
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold tracking-tight">Team alerts</h2>
      <p className="text-sm text-muted-foreground">
        These go out as things happen, whether lead automation is on or off.
      </p>
      <NotificationCard title="To our team" recipient="Email to the team notifications inbox" items={TEAM_ALERTS} />
    </div>
  );
}

const AutomationsPage = () => {
  // #630: the automations flag gates the page, not just the sidebar link, so a
  // staff/admin with the module off is redirected rather than reaching it by URL.
  const { gate } = useModuleAccess("automations", "Automations");
  const { getText } = useBranding();
  const enabled = useSetting("automation_enabled", "false") === "true";

  // Same business identity the engine uses at send time (site_branding, with
  // the engine's own fallbacks). Sample lead name so the preview reads real.
  const vars: TemplateVars = useMemo(
    () => ({
      firstName: "Sarah",
      eventNumber: "EV-2026-0042",
      eventType: "",
      eventLabel: "",
      businessName: getText("identity.name", "Tristano's Catering"),
      businessEmail: getText("identity.email", "hello@tristanoscatering.ca"),
      businessPhone: getText("identity.phone", ""),
    }),
    [getText],
  );

  if (gate) return gate;

  const partialStep = SEQUENCES[PARTIAL_LEAD_SEQUENCE_KEY].steps[0];
  const partial = renderStep(partialStep, { ...vars, eventType: "wedding" });

  return (
    <DashboardLayout title="Automations">
      <div className="max-w-3xl space-y-6">
        <p className="text-sm text-muted-foreground">
          These are the automatic messages we send to new leads, plus the quote
          notifications and the alerts our team gets. Read-only for now —
          editing comes later.
        </p>

        <Alert variant={enabled ? "default" : undefined}>
          {enabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
          <AlertTitle>{enabled ? "Sending is ON" : "Sending is OFF"}</AlertTitle>
          <AlertDescription>
            {enabled
              ? "Leads are receiving these messages automatically."
              : "No lead messages are being sent yet. These are the messages that will go out once automation is turned on. Team alerts below send either way."}
          </AlertDescription>
        </Alert>

        {/* One card per event type: the instant reply, then Emma takes over. */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold tracking-tight">When a new lead comes in</h2>
          <p className="text-sm text-muted-foreground">Wizard leads get the menu link for their service.</p>
          {EVENT_TYPES.map(({ key, label }) => {
            return (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-2 text-base">
                    <span>{label}</span>
                    <Badge variant="secondary" className="font-normal">
                      {cadenceSummary(NEW_LEAD_STEPS)}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ol className="space-y-3">
                    {NEW_LEAD_STEPS.map((step, i) => {
                      const r = renderStep(step, { ...vars, eventType: key, ...previewMenuVars(key, label) });
                      const Icon = step.channel === "sms" ? MessageSquare : Mail;
                      return (
                        <li key={step.index} className="flex items-start gap-3">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <Accordion type="single" collapsible>
                              <AccordionItem value="step" className="border-none">
                                <AccordionTrigger className="py-0 text-sm hover:no-underline">
                                  <span className="flex items-center gap-2 text-left font-normal">
                                    <span className="font-medium">{dayLabel(step)}</span>
                                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                                    {step.channel === "sms" ? (
                                      <span>Text</span>
                                    ) : (
                                      <span>
                                        Email: <span className="font-medium">{r?.channel === "email" ? r.subject : "(missing)"}</span>
                                      </span>
                                    )}
                                  </span>
                                </AccordionTrigger>
                                <p className="mt-1 text-xs text-muted-foreground">{timingLabel(step)}</p>
                                <AccordionContent className="pt-3">
                                  {r?.channel === "email" && <EmailPreview subject={r.subject} html={r.html} />}
                                  {r?.channel === "sms" && <SmsPreview message={r.message} />}
                                  {!r && <p className="text-sm text-destructive">Copy missing for this type.</p>}
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                  <div className="flex items-center gap-3 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground">
                    <Bot className="h-4 w-4 shrink-0" />
                    <span>
                      Follow-up after this is handled by <span className="font-medium">Emma</span>,
                      our messaging assistant. <Badge variant="outline" className="ml-1 font-normal">Coming soon</Badge>
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Abandoned-form nudge */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold tracking-tight">When someone starts the form but doesn't finish</h2>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-base">
                <span>Unfinished request</span>
                <Badge variant="secondary" className="font-normal">
                  <Mail className="mr-1 h-3 w-3" />
                  1 reminder email
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-sm text-muted-foreground">
                Sent about an hour after they leave, only if they gave us a name and email.
                Never more than once, and never by text.
              </p>
              {partial?.channel === "email" ? (
                <EmailPreview subject={partial.subject} html={partial.html} />
              ) : (
                <p className="text-sm text-destructive">Copy missing.</p>
              )}
            </CardContent>
          </Card>
        </div>

        <QuoteNotificationsSection />

        <TeamAlertsSection />
      </div>
    </DashboardLayout>
  );
};

export default AutomationsPage;
